import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ArrowDown, ArrowUp, Eye, EyeOff, Settings2 } from 'lucide-react'

import { useAppStore } from '@core/store'
import { ICONES_DA_BARRA, alternarPainel, painelAberto } from './painelDaBarra'
import { useAuth } from '@core/auth/useAuth'
import { modulosNaInterface } from '@core/partilhas/partilhasRegras'
import {
  RAIL_ITEMS,
  type RailItem,
  alternarEscondido,
  gravarPreferencias,
  itensVisiveis,
  lerPreferencias,
  mover,
} from '@core/layout/rail/railItems'


/** Régua de painéis: uma coluna com tudo o que se pode abrir.
 *
 *  Até aqui era preciso saber em que separador do friso estava o
 *  interruptor de cada painel, e os módulos nem no friso estavam. A
 *  régua não substitui o friso: o friso é para comandos, a régua é
 *  para sítios.
 *
 *  A ordem e o que está escondido ficam guardados por browser. Não há
 *  arrastar: subir e descer resolve o mesmo e funciona com o teclado. */
export default function PanelRail() {
  const navigate = useNavigate()
  const location = useLocation()
  const { workspace, modules: atribuidos, partilhados } = useAuth()
  const modules = modulosNaInterface(atribuidos, partilhados)
  const [prefs, setPrefs] = useState(lerPreferencias)
  const [aPersonalizar, setAPersonalizar] = useState(false)

  const store = useAppStore()
  const visiveis = itensVisiveis(prefs, modules)

  const guardar = (novas: typeof prefs) => {
    setPrefs(novas)
    gravarPreferencias(novas)
  }

  /** Está aberto? Para o ícone poder mostrar-se activo. */
  const aberto = (item: RailItem): boolean =>
    painelAberto(store, item.id) ??
    (item.slug !== undefined && location.pathname.includes(`/${item.slug}`))


  const alternar = (item: RailItem) => {
    if (item.tipo === 'modulo' && item.slug) {
      // Sem workspace (modo local) os módulos não têm rota. O ícone
      // nem chega a aparecer, porque `modules` vem vazio.
      if (workspace) navigate(`/${workspace.slug}/${item.slug}`)
      return
    }
    alternarPainel(store, item.id)
  }

  return (
    // A largura tem de dar para o botão E para a barra de deslocação.
    // Estava em w-11 (44 px) com botões de 36 e uma barra de 10: os
    // botões ficavam 2 px largos de mais para o espaço que sobrava, e
    // aparecia uma barra horizontal numa régua de ícones.
    //
    // O que torna isto traiçoeiro é a regra do CSS: pôr só `overflow-y`
    // faz o `overflow-x` passar de `visible` a `auto` sozinho. Bastam
    // 2 px de excesso para nascer uma barra que ninguém pediu. Por isso
    // vai também o `overflow-x-hidden`, que não é redundância: é o que
    // impede o mesmo defeito de voltar se alguém mexer nos tamanhos.
    <aside
      data-tour="regua"
      className="flex w-12 shrink-0 flex-col items-center gap-0.5 overflow-y-auto overflow-x-hidden border-r border-[var(--color-border)] bg-[var(--color-panel)] py-1"
    >
      {visiveis.map((item) => {
        const Icone = ICONES_DA_BARRA[item.id]
        const activo = aberto(item)
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => alternar(item)}
            title={`${item.label}: ${item.descricao}`}
            aria-label={item.label}
            aria-pressed={activo}
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded ${
              activo
                ? 'bg-[var(--color-accent-bg)] text-[var(--color-accent)]'
                : 'text-[var(--color-text-muted)] hover:bg-[var(--color-surface)] hover:text-[var(--color-text)]'
            }`}
          >
            <Icone size={17} aria-hidden />
          </button>
        )
      })}

      <div className="mt-auto pt-1">
        <button
          type="button"
          onClick={() => setAPersonalizar(true)}
          title="Personalizar a régua: ordem e o que aparece"
          aria-label="Personalizar a régua"
          className="flex h-9 w-9 items-center justify-center rounded text-[var(--color-text-muted)] hover:bg-[var(--color-surface)] hover:text-[var(--color-text)]"
        >
          <Settings2 size={16} aria-hidden />
        </button>
      </div>

      {/* Banda dos diálogos (CAMADA.dialogos), ver core/layout/camadas.ts. */}
      {aPersonalizar && (
        <div
          className="fixed inset-0 z-[1000] flex items-start justify-center bg-black/40 pt-16"
          onClick={() => setAPersonalizar(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Personalizar a régua"
            className="max-h-[80vh] w-[420px] overflow-auto rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-3 shadow-2xl"
          >
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium text-[var(--color-text)]">
                Personalizar a régua
              </span>
              <button
                type="button"
                onClick={() => guardar({ ordem: [], escondidos: [] })}
                className="rounded border border-[var(--color-border)] px-2 py-0.5 text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
              >
                Repor
              </button>
            </div>
            <ul className="space-y-0.5">
              {itensVisiveis(
                { ordem: prefs.ordem, escondidos: [] },
                modules,
              ).map((item) => {
                const Icone = ICONES_DA_BARRA[item.id]
                const escondido = prefs.escondidos.includes(item.id)
                return (
                  <li
                    key={item.id}
                    className="flex items-center gap-2 rounded px-1 py-1 hover:bg-[var(--color-panel)]"
                  >
                    <Icone
                      size={15}
                      className="shrink-0 text-[var(--color-text-muted)]"
                      aria-hidden
                    />
                    <span
                      className={`min-w-0 flex-1 truncate text-xs ${
                        escondido
                          ? 'text-[var(--color-text-dim)] line-through'
                          : 'text-[var(--color-text)]'
                      }`}
                    >
                      {item.label}
                    </span>
                    <button
                      type="button"
                      aria-label={`Subir ${item.label}`}
                      onClick={() =>
                        guardar({
                          ...prefs,
                          ordem: mover(prefs.ordem, item.id, -1),
                        })
                      }
                      className="rounded p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                    >
                      <ArrowUp size={13} aria-hidden />
                    </button>
                    <button
                      type="button"
                      aria-label={`Descer ${item.label}`}
                      onClick={() =>
                        guardar({
                          ...prefs,
                          ordem: mover(prefs.ordem, item.id, 1),
                        })
                      }
                      className="rounded p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                    >
                      <ArrowDown size={13} aria-hidden />
                    </button>
                    <button
                      type="button"
                      aria-label={
                        escondido ? `Mostrar ${item.label}` : `Esconder ${item.label}`
                      }
                      onClick={() =>
                        guardar({
                          ...prefs,
                          escondidos: alternarEscondido(prefs.escondidos, item.id),
                        })
                      }
                      className="rounded p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                    >
                      {escondido ? <EyeOff size={13} aria-hidden /> : <Eye size={13} aria-hidden />}
                    </button>
                  </li>
                )
              })}
            </ul>
            <p className="mt-2 text-[10px] text-[var(--color-text-dim)]">
              A ordem e o que está escondido ficam guardados neste browser.
              Entradas novas da aplicação entram sempre no fim.
            </p>
          </div>
        </div>
      )}
    </aside>
  )
}

export { RAIL_ITEMS }
